import { useEffect, useMemo, useState } from 'react';
import { Grid, Search, X } from 'react-feather';
import { Button, IconButton, Modal } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { WidgetInstance } from '@trinity/chat';
import type { WidgetSpecificConfig } from '@trinity/chat/types/dashboard-config';
import type { WidgetMapContextValue } from '@trinity/chat-components/containers/WidgetGrid/context';
import { ActivityIndicator } from '../../../presentation/index.js';
import type { PaletteEntry } from '../PaletteContext.js';
import { EntryCard, LibraryCard, TemplateCard } from './Cards.js';
import {
  ADD_WIDGET_CATEGORIES,
  buildPickerItems,
  chartTypeLabel,
  chipSubtypes,
  countByCategory,
  enabledCategories,
  filterPickerItems,
  recentPickerItems,
  type AddWidgetCategoryId,
  type AddWidgetMode,
  type PickerItem,
} from './catalog.js';
import { useRecentItems } from './useRecentItems.js';

export type AddWidgetPageProps = {
  open: boolean;
  onClose: () => void;
  /** `library` picks saved project widgets (with data); `new` picks a widget type to add data to next. */
  mode: AddWidgetMode;
  /** What the builder offers; decides which categories and cards appear. */
  entries: readonly PaletteEntry[];
  /** Hides the Section card (used inside sections, which can't nest). */
  hideSections?: boolean;
  /** The project's saved widgets; undefined while loading. Used in `library` mode. */
  libraryWidgets?: readonly WidgetInstance[];
  widgetComponentMap?: WidgetMapContextValue;
  /** Refreshes the saved widgets each time the page opens. Must be stable between renders. */
  onRefetchLibrary?: () => void;
  /** A type card was picked: a template, or a blank Text / Section / other entry. */
  onSelectType: (type: string, config?: Partial<WidgetSpecificConfig>) => void;
  /** A saved widget was picked. */
  onSelectLibraryWidget: (widget: WidgetInstance) => void;
  /** Names the kind of builder, so each keeps its own Recent row; without it there is no Recent row. */
  recentScope?: string;
};

/**
 * The shared "Add widget" picker, replacing the small palette dialog. It is a modal centred over the
 * builder, taking 75% of the screen, and is the same in every builder: what differs is the `mode` and
 * the `entries` the builder passes (see `catalog.ts`).
 */
export function AddWidgetPage(props: AddWidgetPageProps): React.ReactElement {
  return (
    <CenteredModal open={props.open} onClose={props.onClose} aria-labelledby="add-widget-title">
      <PageRoot role="dialog" aria-modal="true" aria-labelledby="add-widget-title">
        <AddWidgetPageContent {...props} />
      </PageRoot>
    </CenteredModal>
  );
}

// The modal's focus trap and Escape handling still apply.
const CenteredModal = styled(Modal)({ display: 'flex', alignItems: 'center', justifyContent: 'center' });

function AddWidgetPageContent({
  onClose,
  mode,
  entries,
  hideSections = false,
  libraryWidgets,
  widgetComponentMap,
  onRefetchLibrary,
  onSelectType,
  onSelectLibraryWidget,
  recentScope,
}: AddWidgetPageProps): React.ReactElement {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<AddWidgetCategoryId | 'all'>('all');
  const [subtype, setSubtype] = useState('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // The page only mounts while open, so this refreshes the saved widgets on every open.
  useEffect(() => {
    onRefetchLibrary?.();
  }, [onRefetchLibrary]);

  const items = useMemo(
    () => buildPickerItems({ mode, entries, libraryWidgets, hideSections }),
    [mode, entries, libraryWidgets, hideSections],
  );
  const railCategories = useMemo(() => {
    const enabled = enabledCategories(entries, mode, hideSections);
    return ADD_WIDGET_CATEGORIES.filter((c) => enabled.has(c.id));
  }, [entries, mode, hideSections]);
  const visible = useMemo(
    () => filterPickerItems(items, { query, category, subtype }),
    [items, query, category, subtype],
  );
  const counts = useMemo(() => countByCategory(items, query), [items, query]);
  const chips = chipSubtypes(items, category, mode);

  // A selection the filters have hidden no longer counts.
  const selected = visible.find((item) => item.id === selectedId) ?? null;
  const loading = mode === 'library' && libraryWidgets === undefined;

  const { recentIds, recordRecent } = useRecentItems(recentScope);
  const recentItems = useMemo(() => recentPickerItems(items, recentIds), [items, recentIds]);
  // The row sits above the full list, so it only shows while that list is unfiltered.
  const showRecent = recentItems.length > 0 && category === 'all' && query.trim() === '' && subtype === 'all';

  const activate = (item: PickerItem): void => {
    recordRecent(item.id);
    const { source } = item;
    if (source.kind === 'library') onSelectLibraryWidget(source.widget);
    else if (source.kind === 'template') {
      onSelectType(source.template.type, source.template.chartType ? { chartType: source.template.chartType } : undefined);
    } else onSelectType(source.entry.type);
    onClose();
  };
  const activateById = (id: string): void => {
    const item = visible.find((candidate) => candidate.id === id);
    if (item) activate(item);
  };
  const chooseCategory = (next: AddWidgetCategoryId | 'all'): void => {
    setCategory(next);
    setSubtype('all');
    setSelectedId(null);
  };

  return (
    <>
      <Header>
        <Title id="add-widget-title">Add a widget</Title>
        <SearchBox>
          <Search aria-hidden />
          <SearchInput
            type="search"
            autoFocus
            autoComplete="off"
            placeholder={mode === 'library' ? 'Search widgets' : 'Search widget types'}
            aria-label={mode === 'library' ? 'Search widgets' : 'Search widget types'}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </SearchBox>
        <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ marginLeft: 'auto' }}>
          <X size={20} />
        </IconButton>
      </Header>

      <Body>
        <Rail aria-label="Widget categories">
          <RailButton type="button" active={category === 'all'} aria-current={category === 'all'} onClick={() => chooseCategory('all')}>
            <Grid size={18} aria-hidden />
            <span className="rail-label">All widgets</span>
            <RailCount className="rail-count">{counts.get('all') ?? 0}</RailCount>
          </RailButton>
          {railCategories.map((c) => (
            <RailButton
              key={c.id}
              type="button"
              active={category === c.id}
              aria-current={category === c.id}
              onClick={() => chooseCategory(c.id)}
            >
              <c.icon size={18} color="currentColor" />
              <span className="rail-label">{c.label}</span>
              <RailCount className="rail-count">{counts.get(c.id) ?? 0}</RailCount>
            </RailButton>
          ))}
        </Rail>

        <Main>
          {chips.length > 0 && (
            <Chips role="group" aria-label="Widget type">
              {['all', ...chips].map((chip) => (
                <ChipButton key={chip} type="button" active={subtype === chip} aria-pressed={subtype === chip} onClick={() => setSubtype(chip)}>
                  {chip === 'all' ? 'All types' : (chartTypeLabel(chip) ?? chip)}
                </ChipButton>
              ))}
            </Chips>
          )}
          <Scroller>
            {!loading && visible.length === 0 && <EmptyState>{emptyMessage(query, mode)}</EmptyState>}
            {!loading && showRecent && (
              <>
                <GroupHeading>Recent</GroupHeading>
                <Section wide={false} role="group" aria-label="Recent">
                  {recentItems.map((item) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      selected={item.id === selected?.id}
                      widgetMap={widgetComponentMap}
                      onSelect={setSelectedId}
                      onActivate={activateById}
                    />
                  ))}
                </Section>
              </>
            )}
            {/* While the library loads, the blank cards (Text, Section) are already usable. */}
            <ItemGroups
              items={visible}
              showHeadings={category === 'all'}
              selectedId={selected?.id ?? null}
              widgetMap={widgetComponentMap}
              onSelect={setSelectedId}
              onActivate={activateById}
            />
            {loading && <ActivityIndicator />}
          </Scroller>
        </Main>
      </Body>

      <Footer>
        <SelectionText>
          {mode === 'new' && <StepBadge>Step 1 of 2</StepBadge>}
          {selected ? (
            <>
              Selected: <b>{selected.title}</b>
              {mode === 'new' && ". You'll add data next."}
            </>
          ) : (
            <Hint>{mode === 'library' ? 'Select a widget. Double-click, or press Enter, to add it.' : "Choose a widget type. You'll connect data in the next step."}</Hint>
          )}
        </SelectionText>
        <CancelButton variant="outlined" onClick={onClose}>
          Cancel
        </CancelButton>
        <PrimaryButton
          variant="contained"
          disabled={!selected}
          onClick={() => {
            if (selected) activate(selected);
          }}
        >
          {mode === 'library' ? 'Add widget' : 'Next: add data →'}
        </PrimaryButton>
      </Footer>
    </>
  );
}

function emptyMessage(query: string, mode: AddWidgetMode): string {
  if (query.trim()) return `No widgets match "${query.trim()}". Try another word or clear the search.`;
  return mode === 'library' ? "There are no saved widgets of this kind in the project's library yet." : 'Nothing to show here.';
}

function ItemGroups({
  items,
  showHeadings,
  selectedId,
  widgetMap,
  onSelect,
  onActivate,
}: {
  items: readonly PickerItem[];
  showHeadings: boolean;
  selectedId: string | null;
  widgetMap: WidgetMapContextValue | undefined;
  onSelect: (id: string) => void;
  onActivate: (id: string) => void;
}): React.ReactElement {
  return (
    <>
      {ADD_WIDGET_CATEGORIES.map((c) => {
        const group = items.filter((item) => item.category === c.id);
        if (group.length === 0) return null;
        return (
          <div key={c.id} style={{ display: 'contents' }}>
            {showHeadings && <GroupHeading>{c.label}</GroupHeading>}
            <Section wide={c.wide ?? false} role="group" aria-label={c.label}>
              {group.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  selected={item.id === selectedId}
                  widgetMap={widgetMap}
                  onSelect={onSelect}
                  onActivate={onActivate}
                />
              ))}
            </Section>
          </div>
        );
      })}
    </>
  );
}

function ItemCard({
  item,
  selected,
  widgetMap,
  onSelect,
  onActivate,
}: {
  item: PickerItem;
  selected: boolean;
  widgetMap: WidgetMapContextValue | undefined;
  onSelect: (id: string) => void;
  onActivate: (id: string) => void;
}): React.ReactElement | null {
  const { source } = item;
  const shared = { item, selected, onSelect, onActivate };
  if (source.kind === 'template') return <TemplateCard {...shared} template={source.template} />;
  if (source.kind === 'entry') return <EntryCard {...shared} entry={source.entry} />;
  return widgetMap ? <LibraryCard {...shared} widget={source.widget} widgetMap={widgetMap} /> : null;
}

const PageRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '75vw',
  height: '75vh',
  overflow: 'hidden',
  borderRadius: 16,
  boxShadow: theme.shadows[24],
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  outline: 'none',
}));

const Header = styled('header')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2.5, 3.5, 2),
  borderBottom: `1px solid ${theme.palette.gray.light2}`,
}));

const Title = styled('h2')({
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  whiteSpace: 'nowrap',
});

const SearchBox = styled('label')(({ theme }) => ({
  position: 'relative',
  display: 'block',
  flex: 1,
  maxWidth: 480,
  '& svg': {
    position: 'absolute',
    left: 12,
    top: 12,
    width: 16,
    height: 16,
    color: theme.palette.text.secondary,
    pointerEvents: 'none',
  },
}));

const SearchInput = styled('input')(({ theme }) => ({
  width: '100%',
  height: 40,
  border: `1px solid ${theme.palette.gray.light2}`,
  borderRadius: 10,
  padding: '0 14px 0 38px',
  font: 'inherit',
  fontSize: 14,
  color: 'inherit',
  backgroundColor: theme.palette.background.default,
  '&:focus': {
    outline: 'none',
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}2E`,
  },
}));

const Body = styled('div')({
  display: 'flex',
  flex: 1,
  minHeight: 0,
});

const Rail = styled('nav')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  flex: 'none',
  width: 220,
  padding: theme.spacing(1.5, 1.25),
  borderRight: `1px solid ${theme.palette.gray.light2}`,
  overflowY: 'auto',
  '@media (max-width: 899px)': { width: 64 },
}));

const RailButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  width: '100%',
  border: 0,
  borderRadius: 10,
  padding: theme.spacing(1.25, 1.5),
  font: 'inherit',
  fontSize: 14,
  fontWeight: active ? 600 : 400,
  textAlign: 'left',
  cursor: 'pointer',
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: active ? `${theme.palette.primary.main}14` : 'transparent',
  '&:hover': { backgroundColor: active ? `${theme.palette.primary.main}14` : theme.palette.action.hover },
  '& svg': { flex: 'none' },
  '@media (max-width: 899px)': {
    justifyContent: 'center',
    // The label stays in the accessibility tree: it is the button's only name once the text is hidden.
    '& .rail-label': {
      position: 'absolute',
      width: 1,
      height: 1,
      margin: -1,
      padding: 0,
      overflow: 'hidden',
      clipPath: 'inset(50%)',
      whiteSpace: 'nowrap',
    },
    '& .rail-count': { display: 'none' },
  },
}));

const RailCount = styled('span')(({ theme }) => ({
  marginLeft: 'auto',
  fontSize: 12,
  color: theme.palette.text.disabled,
}));

const Main = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
});

const Chips = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  padding: theme.spacing(1.75, 3, 0),
}));

const ChipButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  border: `1px solid ${active ? theme.palette.primary.main : theme.palette.gray.light2}`,
  borderRadius: 999,
  padding: '5px 12px',
  font: 'inherit',
  fontSize: 12.5,
  cursor: 'pointer',
  color: active ? '#fff' : theme.palette.text.secondary,
  backgroundColor: active ? theme.palette.primary.main : theme.palette.background.paper,
  '&:hover': { borderColor: theme.palette.primary.main },
}));

const Scroller = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.25),
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(2.25, 3),
  scrollbarWidth: 'thin',
}));

const GroupHeading = styled('div')(({ theme }) => ({
  marginBottom: -4,
  fontSize: 12,
  color: theme.palette.text.disabled,
}));

const Section = styled('div', {
  shouldForwardProp: (prop) => prop !== 'wide',
})<{ wide: boolean }>(({ theme, wide }) => ({
  display: 'grid',
  alignContent: 'start',
  gap: theme.spacing(2),
  gridTemplateColumns: `repeat(${wide ? 2 : 3}, minmax(0, 1fr))`,
}));

const EmptyState = styled('div')(({ theme }) => ({
  padding: theme.spacing(7.5, 0),
  textAlign: 'center',
  fontSize: 14,
  color: theme.palette.text.secondary,
}));

const Footer = styled('footer')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.75, 3.5),
  borderTop: `1px solid ${theme.palette.gray.light2}`,
}));

const SelectionText = styled('div')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 13,
  color: theme.palette.text.secondary,
  '& b': { color: theme.palette.text.primary },
  '@media (max-width: 599px)': { display: 'none' },
}));

const StepBadge = styled('span')(({ theme }) => ({
  display: 'inline-block',
  marginRight: theme.spacing(1.25),
  padding: '2px 9px',
  borderRadius: 999,
  fontSize: 11.5,
  fontWeight: 600,
  color: theme.palette.primary.main,
  backgroundColor: `${theme.palette.primary.main}14`,
}));

const Hint = styled('span')(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.disabled,
}));

const CancelButton = styled(Button)(({ theme }) => ({
  borderWidth: '1px',
  borderColor: theme.palette.gray.light2,
  color: theme.palette.text.primary,
  textTransform: 'none',
  fontWeight: 500,
  height: 40,
  padding: theme.spacing(0, 2.75),
  borderRadius: 10,
  backgroundColor: 'transparent',
  '&:hover': { borderWidth: '1px', borderColor: theme.palette.gray.main, backgroundColor: theme.palette.action.hover },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  height: 40,
  padding: theme.spacing(0, 2.75),
  borderRadius: 10,
}));
